import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
    HostListener,
} from "@angular/core";
import { Route, Router } from "@angular/router";
import {
    routeAnimations,
    ROUTE_ANIMATIONS_ELEMENTS,
} from "@core/animations/route.animations";
import { AuthState } from "@ngxs-store/auth-store/auth.state";
import { Store } from "@ngxs/store";
import { UiState } from "@ngxs-store/ui-store/ui.state";
import { CloseMenuAction, OpenMenuAction, UpdateIsMobileMode } from "@ngxs-store/ui-store/ui.actions";

@Component({
    selector: "app-layout-sidenav-container",
    templateUrl: "./layout-sidenav-container.component.html",
    styleUrls: ["./layout-sidenav-container.component.scss"],
    animations: [routeAnimations],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutSidenavContainerComponent implements OnInit {
    @Input() sidenavRoutes: RouteWithData[];
    @Input() currentRoute: string;
    @Input() showMenu: boolean;
    @Input() isMobileMode: boolean = false;
    @Output() closeMenuClick = new EventEmitter();

    routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
    expandedRoute: string | null = null;
    trackingExpanded = false;
    redirectLink: string;
    hasCustomerAdminRole: boolean = false;
    hasControlTowerRole: boolean = false;
    hasCustomsRole: boolean = false;
    isTrackingExpanded = false;
    isControlTowerExpanded = false;
    isCustoms = false;
    clickItem: string = "";
    constructor(private store: Store, private router: Router) {}

    ngOnInit() {
        this.detectMobileMode();
        this.isMobileMode = this.store.selectSnapshot(UiState.getIsMobileMode);

        this.clickItem = this.getSelectedItem();
        const token = this.store.selectSnapshot(AuthState.getDecodedToken);
        if (
            token.roles.findIndex(
                (hasRoleFn) =>
                    hasRoleFn === "CustomerAdmin" || hasRoleFn === "Customer"
            ) > -1
        ) {
            this.hasCustomerAdminRole = true;
        } else {
            this.hasCustomerAdminRole = false;
        }

        if (
            token.roles.findIndex(
                (hasRoleFn) =>
                    hasRoleFn === "ControlTower"
            ) > -1
        ) {
            this.hasControlTowerRole = true;
        } else {
            this.hasControlTowerRole = false;
        }

        if (
            token.roles.findIndex(
                (hasRoleFn) =>
                    hasRoleFn === "Customs"
            ) > -1
        ) {
            this.hasCustomsRole = true;
        } else {
            this.hasCustomsRole = false;
        }

        const storedMenuState = localStorage.getItem('menuState');
        const currentRoute = this.router.url;

        // Ensure the menu stays closed on /dashboard and /control-tower-dashboard
        if (currentRoute === '/dashboard' || currentRoute === '/control-tower-dashboard' || currentRoute === '/control-tower-live-map'|| currentRoute === '/'
        || currentRoute === '/reports' || currentRoute === '/users' || currentRoute === '/log-tickets' || currentRoute === '/quotes' || currentRoute === '/tours' || currentRoute === '/tour-history'
        || currentRoute === '/adminTours' || currentRoute === '/control-tower-escalation') {
          this.store.dispatch(new CloseMenuAction());
        } else {
          const isMenuOpen = storedMenuState === 'true';
          if (isMenuOpen) {
            this.store.dispatch(new OpenMenuAction());
          } else {
            this.store.dispatch(new CloseMenuAction());
          }
        }
    }

    closeMenu() {
        this.closeMenuClick.emit();
    }

    handleMenuItemClick() {
        if (this.isMobileMode) {
            this.showMenu = false; // Close the sidenav on mobile after a menu item click
        }
    }

    clickSidenavItem(item: string) {
        if (this.isMobileMode) {
            this.closeMenu();
        }

        this.saveSelectedItem(item);
    }

    @HostListener('window:resize', [])
    onResize() {
        this.detectMobileMode();
    }

    detectMobileMode() {
        const isMobile = window.innerWidth <= 768;
        this.store.dispatch(new UpdateIsMobileMode(isMobile));
    }

    expandPanel(panel: string) {
        if (panel === 'tracking') {
          this.isTrackingExpanded = true;
          this.isControlTowerExpanded = false;
          this.isCustoms = false;
        } else if (panel === 'controlTower') {
            this.isControlTowerExpanded = true;
            this.isCustoms = false;
            this.isTrackingExpanded = false;
        } else if(panel === 'customs'){
            this.isCustoms = true;
            this.isControlTowerExpanded = false;
            this.isTrackingExpanded = false;
        }
    }

    collapsePanel(panel: string) {
        if (panel === 'tracking') {
          this.isTrackingExpanded = false;
        } else if (panel === 'controlTower') {
          this.isControlTowerExpanded = false;
        }else if(panel === 'customs'){
            this.isCustoms = false;
        }
    }

    shouldBeExpanded() {
        return !["/home", "/users", "/reports", "/controltower"].includes(this.currentRoute);
    }

    shouldNotBeExpanded() {
        return !["/home", "/users", "/reports", "/tracking"].includes(this.currentRoute);
    }

    toggleSubItems(route: RouteWithData) {
        if (route.data.url === "/tracking") {
            this.trackingExpanded = !this.trackingExpanded;
        } else {
            this.expandedRoute =
                this.expandedRoute === route.data.url ? null : route.data.url;
        }
    }

    toggleMenu(): void {
        this.showMenu = !this.showMenu;
    }

    isRouteExpanded(route: RouteWithData): boolean {
        return route.data.url === "/tracking"
            ? this.trackingExpanded
            : this.expandedRoute === route.data.url;
    }

    saveSelectedItem(item: string): void {
        this.clickItem = item;
        sessionStorage.setItem("clickItem", JSON.stringify(item));
    }

    // Retrieve selected item from sessionStorage
    getSelectedItem(): any {
        const savedItem = sessionStorage.getItem("clickItem");
        return savedItem ? JSON.parse(savedItem) : "";
    }
}

export interface RouteWithData extends Route {
    data: any;
}
